import { useState, useEffect } from 'react';
import bannerConfig from '../config/banners.json';

// Inline SVG icons to avoid dependency
const AlertTriangleIcon = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
    <path d="M12 9v4"/>
    <path d="M12 17h.01"/>
  </svg>
);

const XIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18"/>
    <path d="m6 6 12 12"/>
  </svg>
);

interface BannerConfig {
  id: string;
  message: string;
  type: 'warning' | 'error' | 'info' | 'success';
  dismissible?: boolean;
  colors?: {
    background?: string;
    text?: string;
    border?: string;
  };
  link?: {
    text: string;
    url: string;
  };
}

interface BannerConfigFile {
  enabled?: boolean;
  banners?: BannerConfig[];
}

// Load banners from JSON config file
const CONFIG = bannerConfig as BannerConfigFile;
const BANNERS_ENABLED = CONFIG.enabled !== false;
const ACTIVE_BANNERS: BannerConfig[] = (CONFIG.banners || []);

const DEFAULT_COLORS: Record<BannerConfig['type'], { background: string; text: string; border: string }> = {
  error: {
    background: 'rgba(239, 68, 68, 0.9)',
    text: '#ffffff',
    border: 'rgba(255, 255, 255, 0.1)',
  },
  warning: {
    background: 'rgba(245, 158, 11, 0.9)',
    text: '#000000',
    border: 'rgba(255, 255, 255, 0.1)',
  },
  info: {
    background: 'rgba(59, 130, 246, 0.9)',
    text: '#ffffff',
    border: 'rgba(255, 255, 255, 0.1)',
  },
  success: {
    background: 'rgba(34, 197, 94, 0.9)',
    text: '#052e16',
    border: 'rgba(255, 255, 255, 0.12)',
  },
};

export function CriticalBanner() {
  const [dismissedBanners, setDismissedBanners] = useState<Set<string>>(new Set());
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Load dismissed banners from localStorage
    const stored = localStorage.getItem('dismissed-banners');
    if (stored) {
      try {
        setDismissedBanners(new Set(JSON.parse(stored) as string[]));
      } catch {
        // Ignore parse errors
      }
    }
  }, []);

  const dismissBanner = (id: string) => {
    setDismissedBanners(prev => {
      const next = new Set(prev);
      next.add(id);
      localStorage.setItem('dismissed-banners', JSON.stringify([...next]));
      return next;
    });
  };

  const visibleBanners = ACTIVE_BANNERS.filter(b => !dismissedBanners.has(b.id));

  if (!BANNERS_ENABLED || visibleBanners.length === 0 || !mounted) return null;

  return (
    <div className="w-full shrink-0">
      {visibleBanners.map(banner => (
        <div
          key={banner.id}
          className="px-4 py-1 flex items-center justify-center gap-2 text-[11px] backdrop-blur-sm border-b"
          style={{
            background: banner.colors?.background ?? DEFAULT_COLORS[banner.type].background,
            color: banner.colors?.text ?? DEFAULT_COLORS[banner.type].text,
            borderColor: banner.colors?.border ?? DEFAULT_COLORS[banner.type].border,
          }}
        >
          <AlertTriangleIcon />
          <span className="font-medium">{banner.message}</span>
          {banner.link && (
            <a
              href={banner.link.url}
              className="underline font-semibold hover:opacity-80"
              target="_blank"
              rel="noopener noreferrer"
            >
              {banner.link.text}
            </a>
          )}
          {banner.dismissible && (
            <button
              onClick={() => dismissBanner(banner.id)}
              className="ml-1 p-0.5 hover:bg-black/10 rounded transition-colors"
              aria-label="Dismiss"
            >
              <XIcon />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
